import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, InputNumber, Popover, Space, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import palette from 'palette';
import { RetentionSettingsValues } from 'pages/MediaCluster/types';
import { useAppDispatch, useAppSelector } from 'hooks/store-hooks';
import systemSelectors from 'store/system/selectors';

const { Text } = Typography;

interface RetentionSettingsButtonProps {
  onSave: (next: Required<RetentionSettingsValues>) => void;
  onReset: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export const RetentionSettingsButton: FC<RetentionSettingsButtonProps> = React.memo(
  ({ onSave, onReset, loading, disabled }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const settings = useAppSelector(systemSelectors.mediaCluster.getMediaClusterSettings);
    const [draft, setDraft] = useState<RetentionSettingsValues | null>(settings || null);

    const onOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);
    };

    const msTooltip = useMemo(
      () => <span style={{ color: palette.black.b1 }}>Значення задається в мілісекундах</span>,
      [t]
    );

    useEffect(() => {
      if (settings) {
        setDraft(settings);
      }
    }, [settings]);

    const canSave =
      draft !== null &&
      draft.lightTzRetentionMs !== undefined &&
      draft.heavyTzRetentionMs !== undefined &&
      draft.violatorsRetentionMs !== undefined;

    const content = (
      <div style={{ width: 360 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Tooltip title={msTooltip} color={palette.white.w1}>
            <InfoCircleOutlined />
          </Tooltip>

          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Text>Період зберігання легкових ТЗ</Text>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={draft?.lightTzRetentionMs}
              disabled={disabled}
              onChange={(v) =>
                setDraft((p) => ({
                  ...p,
                  lightTzRetentionMs: (v ?? undefined) as number | undefined,
                }))
              }
              placeholder="Введіть число"
            />
          </Space>

          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Text>Період зберігання вантажних ТЗ</Text>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={draft?.heavyTzRetentionMs}
              disabled={disabled}
              onChange={(v) =>
                setDraft((p) => ({
                  ...p,
                  heavyTzRetentionMs: (v ?? undefined) as number | undefined,
                }))
              }
              placeholder="Введіть число"
            />
          </Space>

          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Text>Період зберігання порушників</Text>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={draft?.violatorsRetentionMs}
              disabled={disabled}
              onChange={(v) =>
                setDraft((p) => ({
                  ...p,
                  violatorsRetentionMs: (v ?? undefined) as number | undefined,
                }))
              }
              placeholder="Введіть число"
            />
          </Space>

          <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={onReset} disabled={disabled} loading={loading}>
              Скинути
            </Button>

            <Button
              type="primary"
              disabled={disabled || !canSave}
              loading={loading}
              onClick={() => {
                if (!canSave || !draft) return;
                onSave({
                  lightTzRetentionMs: draft.lightTzRetentionMs!,
                  heavyTzRetentionMs: draft.heavyTzRetentionMs!,
                  violatorsRetentionMs: draft.violatorsRetentionMs!,
                });
                setOpen(false);
              }}
            >
              Зберегти
            </Button>
          </Space>
        </Space>
      </div>
    );

    return (
      <Popover visible={open} onVisibleChange={onOpenChange} trigger="click" placement="bottomRight" content={content}>
        <Tooltip title={<span style={{ color: palette.black.b1 }}>Налаштування</span>} color={palette.white.w1}>
          <Button type="text" icon={<SettingOutlined />} />
        </Tooltip>
      </Popover>
    );
  }
);
